import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@emotion/react";
import dayjs from "dayjs";
import { showToast } from "services/utils/Status";
import {
  checkEditorPermission,
  checkViewerPermission,
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
  getAdministrationRedirectPath,
  isProvisioningLoaded,
} from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { setGlobalSearchValue } from "services/Redux/userToken";
import { useSelector, useDispatch } from "react-redux";
import {
  employeeListKeys,
  defaultAllSelection,
  adminRoleName,
  supportLabel,
} from "services/constants";
import { customGetAPI } from "services/utils/CustomAPI";
import { canUseCompanyContext } from "services/utils/companyAccess";
import sessionHandling from "services/utils/notificationUtils";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { ListView } from "./ListView";
import { UserStatCards } from "./UserStatCards";
import { UserDetailPanel } from "./UserDetailPanel";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { FilterProvider } from "pages/FilterContext";
import { adminUi, usersHeaderRow } from "./adminListScreenUi";
import { designTheme } from "services/constants/designTokens";
import { useExpandablePanel } from "services/hooks/useExpandablePanel";
import { CreateUser } from "./CreateUser";

// Shared by updateMenuItem (which drives the real, multi-item "More
// Actions" dropdown) and the panel's own render (which needs to know ahead
// of time whether that dropdown would only ever contain one item, so it
// can show that one action directly instead of forcing an extra click
// through a menu with nothing else in it).
const getUserDropdownOptions = (status, role, canEdit, canApprove) => {
  switch (status) {
    case "Invited":
    case "Expired":
      return canEdit ? [{ dropMenu: "Resend" }] : [];
    case "Active": {
      // Deactivate is deliberately left out -- the panel already has a
      // standalone "Deactivate User" button that does the exact same
      // thing, so listing it again here would be a pure duplicate.
      const options = [{ dropMenu: "View Profile" }];
      if (canApprove) {
        if ([adminRoleName, supportLabel].includes(role)) {
          options.push({ dropMenu: "Make User" });
        } else if (role === "User" || role === "Team Lead") {
          options.push({ dropMenu: "Make Super Admin" });
        }
      }
      return options;
    }
    case "Inactive":
      // "Active" left out for the same reason -- the standalone button
      // already reads as "Activate User" once the user is inactive and
      // calls the same handler.
      return [{ dropMenu: "View Profile" }];
    default:
      return [];
  }
};

export const Users = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    domain,
    userScreenAccess,
    globalSearchValue,
    companyList,
    token,
    userDetails,
    topbarCompanyId,
  } = useSelector((state) => state.tokenReducer);
  const [fetchUserDetails, setFetchUserDetails] = useState(true);
  const [masterDataUserDetails, setMasterDataUserDetails] = useState([]);
  const [companyDetails, setCompanyDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editUser, setEditUserDetails] = useState(null);
  const [dropDown, setDropDown] = useState([]);
  const [filterChanges, setFilterChanges] = useState(false);
  const [openEmail, setOpenEmail] = useState(false);
  const [openRevokeDialog, setOpenRevokeDialog] = useState(false);
  const [openDeactivatePopup, setOpenDeactivatePopup] = useState(false);
  const [actionOption, setActionOption] = useState("");
  const [dataViewFormat, setDataViewFormat] = useState("ListView");
  const [buttonLoader, setButtonLoader] = useState(false);
  const [page, setPage] = useState(0);
  const [panelUser, setPanelUser] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelEditMode, setPanelEditMode] = useState(false);
  const [panelProfileMode, setPanelProfileMode] = useState(false);
  const [statusCardFilter, setStatusCardFilter] = useState(null);
  const [createUserPanelOpen, setCreateUserPanelOpen] = useState(false);
  const { expanded: userPanelExpanded, toggleExpanded: toggleUserPanelExpanded } =
    useExpandablePanel(panelOpen);
  const { expanded: createUserExpanded, toggleExpanded: toggleCreateUserExpanded } =
    useExpandablePanel(createUserPanelOpen);

  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Users",
    "Users",
  );

  // Users has no real approval workflow, but the permission flag still
  // exists on the submodule -- per the same edit-vs-approve split just
  // applied to Companies, the status-changing actions (Deactivate/Active,
  // Make Super Admin/Make User) require this instead of plain editor
  // access, while Resend stays editor-gated and the redundant "Edit" menu
  // item (duplicating the panel's own Edit button) is removed outright.
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Users",
      "Users",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Users",
      "Users",
    );

  // Sidebar hiding the "Users" link when viewer access is revoked
  // (ADMINISTRATION_VIEWER_GATED_MODULES) only hides the menu row -- there's
  // no per-route access guard anywhere in the app, so refreshing while
  // already on this screen still rendered the full user list with no check
  // at all. Same fix as Companies/Roles/User Group/Notification Settings.
  const hasViewAccess = checkViewerPermission(
    userScreenAccess?.module_details,
    "Users",
    "Users",
  );

  // Redux's userScreenAccess starts out as last session's cached value (or
  // the empty array default) until this page's own provisioning fetch
  // resolves -- see isProvisioningLoaded. Without this gate, a hard refresh
  // redirected away immediately off that stale/empty snapshot, before the
  // real fetch had a chance to answer, which looked like the provisioning
  // API was never even called.
  const provisioningLoaded = isProvisioningLoaded(userScreenAccess, "Administration");

  // A static "Access Restricted" message was a dead end -- send the user
  // somewhere they can actually see instead (another Administration screen
  // first, then the same "first accessible product" fallback the login flow
  // already uses). Runs as an effect, not during render, since navigating
  // is a side effect.
  useEffect(() => {
    if (!provisioningLoaded || hasViewAccess) return;
    history.replace(
      getAdministrationRedirectPath(userScreenAccess?.module_details),
    );
  }, [provisioningLoaded, hasViewAccess, userScreenAccess, history]);

  const usersModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Users",
  );

  const subModuleId = usersModule?.submodules?.find(
    (sub) => sub.sub_module_name === "Users",
  )?.sub_module_id;

  useEffect(() => {
    dispatch(setGlobalSearchValue(""));
  }, [dispatch]);

  useEffect(() => {
    if (topbarCompanyId) {
      setFetchUserDetails(true);
    }
  }, [topbarCompanyId]);

  useEffect(() => {
    const getCompany = async () => {
      const res = await customGetAPI(`${domain}get-active-company/`);
      if (res.ok) {
        if (res?.responseDetails?.length === 0) {
          // The "create the company" prompt is now the last step of the
          // global setup flow (components/SetupFlow).
          setDataViewFormat("CardView");
        }
        const allCompanies = {
          id: defaultAllSelection,
          label: "All Companies",
          companyname: "All Companies",
        };
        setCompanyDetails([allCompanies, ...res?.responseDetails]);
      }
    };
    getCompany();
  }, [domain, token]);

  const effectiveCompanyId = topbarCompanyId;

  useEffect(() => {
    if (!fetchUserDetails || !provisioningLoaded || !hasViewAccess) return undefined;
    if (!canUseCompanyContext(effectiveCompanyId, userDetails?.type, companyList)) {
      setMasterDataUserDetails([]);
      setAllUsers([]);
      setIsLoading(false);
      setFetchUserDetails(false);
      return undefined;
    }
    let cancelled = false;
    const fetchAllUsers = async () => {
      setIsLoading(true);
      setMasterDataUserDetails([]);
      setAllUsers([]);
      try {
        const res = await customGetAPI(
          `${domain}new_user_details/?company_id=${effectiveCompanyId}`,
        );
        if (cancelled) return;
        const responseDetails = res?.ok && Array.isArray(res.responseDetails) ? res.responseDetails : [];
        setMasterDataUserDetails(responseDetails);
        setAllUsers(responseDetails);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setFetchUserDetails(false);
        }
      }
    };
    fetchAllUsers();
    return () => { cancelled = true; };
  }, [companyList, domain, effectiveCompanyId, fetchUserDetails, hasViewAccess, provisioningLoaded, token, userDetails?.type]);

  useEffect(() => {
    // Reuse masterDataUserDetails' own reference when there's no active
    // global search — .filter() always returns a new array even when it
    // excludes nothing, and that new (but content-identical) reference was
    // enough to re-trigger CustomFilter's and ListView's own effects a
    // second time right after the real fetch had already landed correctly.
    // That second round could settle filteredUsers (Administration/
    // ListView.js) as non-null-but-momentarily-stale relative to the
    // newest allUsers — a gap the null/?? guard there only covers on the
    // very first render, not this reference churn — flashing "No Matching
    // Results" right after the real rows had already shown once.
    const filteredResults = globalSearchValue
      ? masterDataUserDetails.filter((item) =>
          employeeListKeys.some((key) =>
            item[key]
              ?.toString()
              .toLowerCase()
              .includes(globalSearchValue.toString().toLowerCase()),
          ),
        )
      : masterDataUserDetails;
    setFilterChanges(true);
    setPage(globalSearchValue ? 0 : page);
    setAllUsers(filteredResults);
  }, [dataViewFormat, globalSearchValue, masterDataUserDetails, page]);

  // "Edit" used to appear in this menu duplicating the panel's own Edit
  // button -- removed outright, same as Companies' "Edit Company Details".
  // Deactivate/Active/Make Super Admin/Make User are approve-gated per the
  // edit-vs-approve split (hasApproverAccess above); Resend stays
  // editor-gated. Hidden here rather than shown and toast-blocked on
  // click, same as everywhere else in Administration.
  const updateMenuItem = (status, role) => {
    setDropDown(getUserDropdownOptions(status, role, check_access, hasApproverAccess));
  };

  // targetUser defaults to editUser (the state the real dropdown sets via
  // onMoreActions before opening) so every existing call site is
  // unaffected. The panel's own direct sole-action button (below) instead
  // passes panelUser explicitly, since setEditUserDetails+call in the same
  // tick wouldn't see the update in time -- state updates aren't
  // synchronous, so this avoids relying on a stale closure over editUser.
  const UpdateUserDetails = async (option, targetUser = editUser) => {
    switch (option) {
      case "Resend":
        if (check_access) {
          try {
            const response = await fetch(
              `${domain}send-registration-email/?role=User&email=${targetUser.email}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
              },
            );
            await response.json();
            if (response.ok) {
              const field = "status";
              const updatedUsers = allUsers.map((element) => {
                if (element.id === targetUser.id) {
                  return { ...element, [field]: "Invited" };
                }
                return element;
              });
              setAllUsers(updatedUsers);
              setAnchorEl(null);
              showToast("success", "Email resend.");
            } else if (response.status === 409) {
              sessionHandling();
            } else {
              throw new Error("REQUEST_FAILED");
            }
          } catch (error) {
            console.error("[Index] operation failed:", error);
            setAnchorEl(null);
            showToast("error", "Couldn't save the details. Please try again.");
          }
        } else {
          showToast(
            "error",
            `Access denied: You are not authorized to resend the link`,
          );
        }
        break;
      case "View Profile":
        setAnchorEl(null);
        setPanelUser(targetUser);
        setPanelEditMode(false);
        setPanelProfileMode(true);
        if (userPanelExpanded) toggleUserPanelExpanded();
        setPanelOpen(true);
        break;
      case "Make Super Admin":
      case "Make User":
        // Approve-gated, not editor -- matches Companies' Primary. This
        // menu item is now hidden without approver access (updateMenuItem
        // above), so this is a direct guard rather than the only one.
        if (hasApproverAccess) {
          setAnchorEl(null);
          try {
            const response = await fetch(`${domain}change/type/`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `token ${token}`,
              },
              body: JSON.stringify({
                user_id: targetUser.id,
                type: option === "Make User" ? "User" : adminRoleName,
              }),
            });
            await response.json();
            if (response.ok) {
              setAnchorEl(null);
              const field = "type";
              setAllUsers([]);
              const updatedUsers = allUsers.map((element) => {
                if (element.id === targetUser.id) {
                  return {
                    ...element,
                    [field]: option === "Make User" ? "User" : adminRoleName,
                  };
                }
                return element;
              });
              setAllUsers(updatedUsers);
              showToast(
                "success",
                `User type changed ${
                  option === "Make User"
                    ? "back to standard User."
                    : "to Super Admin."
                }`,
              );
            } else if (response.status === 409) {
              sessionHandling();
            } else {
              throw new Error("REQUEST_FAILED");
            }
          } catch (error) {
            console.error("[Index] operation failed:", error);
            showToast("error", "Couldn't save the details. Please try again.");
          } finally {
            setAnchorEl(null);
          }
        } else {
          // Was copy-pasted from the Resend branch above ("...resend the
          // link") -- corrected while updating this case's access check.
          showToast(
            "error",
            "Access denied: You are not authorized to change the user's type",
          );
        }
        break;
      case "Invite System Access":
        setAnchorEl(null);
        setOpenEmail(true);
        setAnchorEl(null);
        break;
      case "Revoke System Access":
        setAnchorEl(null);
        setOpenRevokeDialog(true);
        setAnchorEl(null);
        break;
      default:
        break;
    }
  };

  const inviteSystemAccess = async (userId, emailId) => {
    if (check_access) {
      setButtonLoader(true);
      try {
        const response = await fetch(`${domain}invite_system_access/`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
          body: JSON.stringify({
            user_id: userId,
            email: emailId,
          }),
        });
        const res = await response.json();
        if (response.ok) {
          showToast("success", res.success);
          setFetchUserDetails(true);
          setOpenEmail(false);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Index] operation failed:", error);
        showToast("error", "Couldn't save the details. Please try again.");
      } finally {
        setButtonLoader(false);
      }
    } else {
      showToast(
        "error",
        "Access denied: You are not authorized to invite system access",
      );
    }
  };

  //Api Integration for Check Existing User

  const checkExistingUser = async (userId) => {
    const res = await customGetAPI(
      `${domain}invoke/system-access/existing-user/?user_id=${userId}`,
    );
    if (res.ok) {
      return res?.responseDetails?.email;
    }
  };

  //Api Integration for Revoke System Access

  const revokeSystemAccess = async (userId, emailId) => {
    if (check_access) {
      setButtonLoader(true);
      try {
        const response = await fetch(`${domain}revoke_system_access/`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
          body: JSON.stringify({
            user_id: userId,
            email: emailId,
          }),
        });
        const res = await response.json();
        if (response.ok) {
          const field = "system_access";
          setAllUsers([]);
          const updatedUsers = allUsers.map((element) => {
            if (element.id === editUser.id) {
              return { ...element, [field]: "No" };
            }
            return element;
          });
          setAllUsers(updatedUsers);
          showToast("success", res.success);
          setOpenRevokeDialog(false);
          setFetchUserDetails(true);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Index] operation failed:", error);
        showToast("error", "Couldn't save the details. Please try again.");
      } finally {
        setButtonLoader(false);
      }
    } else {
      showToast(
        "error",
        "Access denied: You are not authorized to revoke system access",
      );
    }
  };

  const handleDeactivateDialogBox = () => {
    setOpenDeactivatePopup(false);
  };

  const deactivateUser = async () => {
    if (check_access) {
      setButtonLoader(true);
      try {
        const nextStatus =
          actionOption === "Deactivate" ? "Inactive" : actionOption;
        const payload = {
          user_id: editUser.id,
          status: nextStatus,
        };
        if (nextStatus === "Active") {
          payload.effective_from = dayjs().format("YYYY-MM-DD");
          payload.effective_to = null;
        }
        const response = await fetch(`${domain}deactivate/`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
          body: JSON.stringify(payload),
        });
        const res = await response.json();
        if (response.ok) {
          if (response.status === 201) {
            setAnchorEl(null);
            if (actionOption === "Active") {
              showToast("success", "User activated.");
              if (res?.warning) {
                showToast("info", res.warning);
              }
            } else if (actionOption === "Deactivate") {
              showToast("error", "User deactivated.");
            }
            const field = "status";
            const updateUserStatus = (users) =>
              users.map((element) => {
                if (element.id === editUser.id) {
                  return {
                    ...element,
                    [field]: nextStatus,
                  };
                }
                return element;
              });
            setAllUsers((prev) => updateUserStatus(prev));
            setMasterDataUserDetails((prev) => updateUserStatus(prev));
            setEditUserDetails((prev) =>
              prev?.id === editUser.id ? { ...prev, [field]: nextStatus } : prev,
            );
            setPanelUser((prev) =>
              prev?.id === editUser.id ? { ...prev, [field]: nextStatus } : prev,
            );
          } else {
            setAnchorEl(null);
            throw new Error(
              `Cannot inactivate team lead when there were employees assigned under him. Either remove or move all the employess under him to proceed further.`,
            );
          }
        } else if (response.status === 409) {
          sessionHandling();
        } else if (response.status === 400 && (res?.error || res?.message)) {
          showToast("error", res.error || res.message);
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Index] operation failed:", error);
        showToast("error", "Couldn't save the details. Please try again.");
      } finally {
        handleDeactivateDialogBox();
        setButtonLoader(false);
      }
    } else {
      showToast(
        "error",
        "Access denied: You are not authorized to deactivate the user",
      );
    }
  };

  const openCreateUserPanel = () => {
    setCreateUserPanelOpen(true);
  };

  const handleStatCardClick = (key) => {
    setPage(0);
    if (key === "all") {
      setStatusCardFilter(null);
    } else {
      setStatusCardFilter((prev) => (prev === key ? null : key));
    }
  };

  const handleEditFromPanel = (userToEdit) => {
    if (!check_access) {
      showToast(
        "error",
        "Access denied: You are not authorized to edit user",
      );
      return;
    }
    setPanelUser(userToEdit);
    setPanelEditMode(true);
    setPanelOpen(true);
  };

  const handleSavePanelEdit = async (userToEdit, data) => {
    if (!check_access) {
      showToast(
        "error",
        "Access denied: You are not authorized to edit user",
      );
      return false;
    }

    try {
      const response = await fetch(`${domain}normal_user_registration/`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify({
          user_id: userToEdit.id,
          phone_number: data.phoneno,
          user_name: data.name,
          email: data.email,
          role: data?.user_type,
          company_id: data.company_id,
        }),
      });
      const res = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          sessionHandling();
          return false;
        }
        showToast(
          "error",
          res?.error ||
            res?.message ||
            "Couldn't save the user details. Please try again.",
        );
        return false;
      }

      const selectedCompany = companyDetails.find(
        (company) => company.id === data.company_id,
      );
      const updatedUser = {
        ...userToEdit,
        user_name: data.name,
        phonenumber: data.phoneno,
        email: data.email,
        type: data.user_type,
        company_id: data.company_id,
        company_name:
          data.company ||
          selectedCompany?.companyname ||
          userToEdit.company_name,
      };

      setAllUsers((prev) =>
        prev.map((item) => (item.id === userToEdit.id ? updatedUser : item)),
      );
      setMasterDataUserDetails((prev) =>
        prev.map((item) => (item.id === userToEdit.id ? updatedUser : item)),
      );
      setPanelUser(updatedUser);
      setEditUserDetails(updatedUser);
      setPanelEditMode(false);
      showToast(
        "success",
        data?.user_type === "User"
          ? "User updated successfully"
          : "Super admin updated successfully",
      );
      return true;
    } catch (error) {
      console.error("[Users] operation failed:", error);
      showToast("error", "Couldn't save the user details. Please try again.");
      return false;
    }
  };

  if (!provisioningLoaded) {
    return <Loader info="Loading..." />;
  }

  if (!hasViewAccess) {
    return <Loader info="Redirecting..." />;
  }

  // const handleCompanyName = (event, data) => {
  //   if (data) {
  //     dispatch(setAdminCompanyName(data.label));
  //     dispatch(setAdminCompanyId(data.id));
  //     localStorage.setItem("adminCompanyId", data.id);
  //     localStorage.setItem("adminCompanyname", data.label);
  //     setFetchUserDetails(true);
  //   } else {
  //     dispatch(setAdminCompanyName(""));
  //   }
  // };

  // What the More Actions menu would contain for the user currently open
  // in the panel, computed ahead of the actual click (unlike updateMenuItem,
  // which only runs once that button is pressed) so the panel can decide
  // whether to show a real "More Actions" dropdown or, when there's only
  // one thing behind it, that one action as a direct button instead --
  // e.g. an edit-only viewer on an Active user only ever gets "View
  // Profile" there, which doesn't need a menu at all.
  const panelMenuOptions = panelUser
    ? getUserDropdownOptions(
        panelUser?.status,
        panelUser?.type,
        check_access,
        hasApproverAccess,
      )
    : [];
  const panelHasMenuOptions = panelMenuOptions.length > 0;
  const panelSoleMenuAction =
    panelMenuOptions.length === 1 ? panelMenuOptions[0].dropMenu : null;

  return (
    <ThemeProvider theme={designTheme}>
      <Mui.Box sx={adminUi.page}>
        <Mui.Box sx={{ px: 2, pt: { xs: 7, sm: 2 } }}>
          {usersHeaderRow({
            title: "Users",
            subtitle: "Manage and view all system users.",
            primaryLabel: "Create User",
            // Hide the button rather than show it and block the click --
            // same treatment TeamZone Approve's NewRequestButton already
            // gives its own create action for a non-editor.
            showPrimaryAction: check_access,
            onPrimaryClick: () => {
              if (check_access) {
                openCreateUserPanel();
              } else {
                showToast(
                  "error",
                  "Access denied: You are not authorized to create user",
                );
              }
            },
          })}
        </Mui.Box>
        <UserStatCards
          users={masterDataUserDetails}
          onCardClick={handleStatCardClick}
          activeFilter={statusCardFilter}
        />
        {dataViewFormat === "ListView" ? (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <ListView
              openRevokeDialog={openRevokeDialog}
              setOpenRevokeDialog={setOpenRevokeDialog}
              openEmail={openEmail}
              setOpenEmail={setOpenEmail}
              checkExistingUser={checkExistingUser}
              inviteSystemAccess={inviteSystemAccess}
              revokeSystemAccess={revokeSystemAccess}
              allUsers={allUsers}
              masterDataUserDetails={masterDataUserDetails}
              isLoading={isLoading}
              updateMenuItem={updateMenuItem}
              dropDown={dropDown}
              editUser={editUser}
              setEditUserDetails={setEditUserDetails}
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              filterChanges={filterChanges}
              setFilterChanges={setFilterChanges}
              UpdateUserDetails={UpdateUserDetails}
              buttonLoader={buttonLoader}
              setPage={setPage}
              page={page}
              onRowClick={(data) => {
                setPanelProfileMode(false);
                setPanelUser(data);
                setPanelEditMode(false);
                setPanelOpen(true);
              }}
              selectedUserId={panelOpen ? panelUser?.id : null}
              statusFilter={statusCardFilter}
              subModuleId={subModuleId}
              // Download/sort/filter/default-view are view-only actions --
              // gate on viewer access, matching TeamZone Reports.
              // check_access (editor) still gates Create User and the
              // panel's own edit action separately.
              viewAccess={hasViewAccess}
            />
          </FilterProvider>
        ) : null}
      </Mui.Box>
      <UserDetailPanel
        open={panelOpen}
        onClose={() => {
          if (panelProfileMode) {
            setPanelProfileMode(false);
            return;
          }
          setPanelOpen(false);
          setPanelEditMode(false);
          setPanelProfileMode(false);
        }}
        expanded={userPanelExpanded}
        onToggleExpand={toggleUserPanelExpanded}
        user={panelUser}
        checkAccess={check_access}
        hasApproverAccess={hasApproverAccess}
        hasMenuOptions={panelHasMenuOptions}
        soleMenuAction={panelSoleMenuAction}
        startInEditMode={panelEditMode}
        profileMode={panelProfileMode}
        onMoreActions={(user, event) => {
          setEditUserDetails(user);
          updateMenuItem(user?.status, user?.type, user?.system_access);
          setAnchorEl(event?.currentTarget || null);
        }}
        onSoleMenuAction={(user) => {
          if (panelSoleMenuAction) UpdateUserDetails(panelSoleMenuAction, user);
        }}
        onEdit={handleEditFromPanel}
        onSaveEdit={handleSavePanelEdit}
        onDeactivate={(user) => {
          // Same status-change action as the More Actions menu's Deactivate/
          // Active item -- approve-gated to match, not editor-gated.
          if (!hasApproverAccess) {
            showToast(
              "error",
              `Access denied: You are not authorized to ${user?.status === "Active" ? "Deactivate" : "Active"} the user`,
            );
            return;
          }
          setEditUserDetails(user);
          setActionOption(user?.status === "Active" ? "Deactivate" : "Active");
          setOpenDeactivatePopup(true);
          setPanelOpen(false);
        }}
      />
      <CreateUser
        open={createUserPanelOpen}
        expanded={createUserExpanded}
        onClose={() => setCreateUserPanelOpen(false)}
        onCreated={() => setFetchUserDetails(true)}
        onToggleExpand={toggleCreateUserExpanded}
      />
      <GlobalConfirmDialog
        open={openDeactivatePopup}
        title={
          actionOption === "Deactivate"
            ? "Deactivate the user"
            : "Activate the user"
        }
        onCancel={handleDeactivateDialogBox}
        onConfirm={deactivateUser}
        loading={openDeactivatePopup && buttonLoader}
        danger={actionOption === "Deactivate"}
      >
        Do you want to{" "}
        {actionOption === "Deactivate" ? "deactivate" : "activate"} the user?
      </GlobalConfirmDialog>
    </ThemeProvider>
  );
};
